import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@rocco/utils/cn';

import { CONTENT_CAP_PADDED } from './large-screen';
import { stripHtml } from './utils';

/** One testimonial as the CMS stores it: HTMLText fields and an order. */
export interface DetailTestimonial {
    ID: number;
    Content?: string | null;
    Source?: string | null;
    Location?: string | null;
    SortOrder: number;
}

export interface DetailTestimonialBandProps {
    /** What has been said about the professional, in the order they arranged it. */
    testimonials: DetailTestimonial[];
    /** The rail's label: "Supplier Reviews", "Professional Reviews". */
    label: string;
    /** `light` = ink on the pale page; `dark` = light type on #181818. */
    theme?: 'light' | 'dark';
    className?: string;
}

/**
 * The pull quote (Figma 2:518), shared by the product and project pages.
 *
 * A product or project carries no testimonials of its own: `Testimonial` has
 * a `has_one` to `Professional` and nothing else. What the page can show is
 * what has been said about the professional behind it, which is why the
 * attribution names a person and their practice rather than the item.
 *
 * One quote at a time, in the order the professional arranged them: the
 * design gives it a full band and 60px type, and a second quote underneath
 * would turn a statement into a list. The arrows in the left rail step
 * through the rest when there are any.
 *
 * Renders nothing when there are no testimonials, which is the common case.
 */
/**
 * The Business Manager's testimonial editor seeds each new row with these, and
 * a supplier who never filled a field keeps the placeholder. Live data has
 * them — every ABI Interiors testimonial carries "New testimonial location" —
 * so they are not content and must never reach the page.
 */
const PLACEHOLDERS = new Set([
    'new testimonial content',
    'new testimonial source',
    'new testimonial location',
]);

/**
 * Content, Source and Location are all HTMLText: Source arrives as
 * `<p>Samantha B&nbsp;</p>`, not a bare name. Everything is reduced to plain
 * text, and a leftover placeholder counts as empty.
 */
const plain = (html: string | null | undefined): string => {
    const text = stripHtml(html ?? '').trim();
    return PLACEHOLDERS.has(text.toLowerCase()) ? '' : text;
};

/**
 * Suppliers type their own quote marks into the body, so the band's marks
 * doubled them up — `““We love our furniture…!"”`. The band supplies the marks,
 * so whatever the content brought is dropped first.
 */
const unquote = (text: string): string =>
    text
        .replace(/^["'\u2018\u2019\u201c\u201d]+/, '')
        .replace(/["'\u2018\u2019\u201c\u201d]+$/, '')
        .trim();

/** `useLayoutEffect` warns on the server; the fit only runs in the browser. */
const useIsomorphicLayoutEffect =
    typeof document === 'undefined' ? useEffect : useLayoutEffect;

/**
 * How much of a long quote shows before the reader asks for the rest.
 *
 * Suppliers write what they write — some of these run to eighty words. An
 * earlier version shrank the type until the whole thing fitted, which kept
 * every quote whole at the cost of the design's size: a long review arrived at
 * 28px next to a short one at 60px, and the band changed voice per supplier.
 * Holding the size and folding the overflow keeps the type where it was drawn
 * and puts the length under the reader's control instead.
 */
const QUOTE_LINES = 4;

/** How far the incoming quote travels, and how long it takes. */
const SLIDE_PX = 28;
const SLIDE_MS = 420;

/** The left rail's arrows — the same disc the page's other chrome uses. */
const arrowClass = (dark: boolean) =>
    cn(
        'inline-flex size-[2.75rem] shrink-0 cursor-pointer items-center justify-center rounded-[1rem]',
        'bg-transparent transition-colors disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent',
        dark
            ? 'border border-white/30 text-white hover:bg-white/10'
            : 'border border-[#ddd8d0] text-[#323232] hover:bg-white',
    );

/**
 * The quotes the band can actually draw, in the order it draws them. A
 * testimonial whose body is empty once stripped of markup renders nothing,
 * so callers that need to know whether the band will appear at all — to
 * decide the space around it — must ask the same question it asks.
 */
const renderableQuotes = (testimonials: DetailTestimonial[]) =>
    testimonials
        .filter((entry) => plain(entry.Content))
        .sort((a, b) => a.SortOrder - b.SortOrder);

/** Whether the band will draw anything for these testimonials. */
export const hasRenderableTestimonial = (
    testimonials: DetailTestimonial[],
): boolean => renderableQuotes(testimonials).length > 0;

export const DetailTestimonialBand = ({
    testimonials,
    label,
    theme = 'light',
    className,
}: DetailTestimonialBandProps) => {
    const dark = theme === 'dark';

    const [index, setIndex] = useState(0);
    const quoteRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLParagraphElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    // Which way the reader asked to go, so the new quote comes in from the
    // side they came from rather than always the same one.
    const direction = useRef(1);
    const stepped = useRef(false);

    // The clamp is measured, not assumed: a quote that runs to three lines
    // should not offer a Read more that reveals nothing, so the control only
    // appears once the text has actually overflowed.
    useIsomorphicLayoutEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        setOverflows(el.scrollHeight > el.clientHeight + 1);
    });

    // The quote enters; it does not also leave. React swaps the text in one
    // pass, so an exit would need the old and the new mounted together — two
    // stacked 60px paragraphs, and a band that changes height as they cross.
    // Bringing the new one in is enough to read as movement.
    useEffect(() => {
        if (!stepped.current) return;
        const node = quoteRef.current;
        if (!node?.animate) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        node.animate(
            [
                {
                    opacity: 0,
                    transform: `translateX(${direction.current * SLIDE_PX}px)`,
                },
                { opacity: 1, transform: 'translateX(0)' },
            ],
            { duration: SLIDE_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
    }, [index]);

    const quotes = renderableQuotes(testimonials);

    const quote = quotes[index] ?? quotes[0];
    if (!quote) return null;

    const body = unquote(plain(quote.Content));
    const source = plain(quote.Source);
    const attribution = plain(quote.Location);
    const step = (delta: number) => {
        direction.current = delta;
        stepped.current = true;
        // Each quote is its own length, so the next one starts folded rather
        // than inheriting this one's expanded state.
        setExpanded(false);
        setOverflows(false);
        setIndex(
            (current) => (current + delta + quotes.length) % quotes.length,
        );
    };

    return (
        <section
            className={cn(
                'max-md:px-6 md:px-[3rem]',
                // The band insets its content from the top on the rail and the
                // figure; without the matching inset underneath, the quote's
                // attribution ends on the band's own last pixel and butts
                // straight into whatever follows it.
                'max-md:pb-8 md:pb-[4.375rem]',
                dark ? 'bg-transparent' : 'bg-ground',
                className,
            )}
        >
            <div className={CONTENT_CAP_PADDED}>
                {/* Indented rather than centred: the quote hangs off the same
                    left margin the In the wild rows use, so the page keeps one
                    editorial column rather than gaining a centred one. */}
                {/* The rule sits on the grid rather than on the quote: it
                    marks where the band starts, and a rule that begins at the
                    quote's own column reads as an underline on the column
                    instead. */}
                <div
                    className={cn(
                        'grid max-md:grid-cols-1 md:grid-cols-[340fr_1180fr] md:gap-[6.25rem]',
                        // The rule marks where the band starts on the pale
                        // product page; the project's dark tail runs its
                        // sections on air alone.
                        !dark && 'border-t border-[#ddd8d0]',
                    )}
                >
                    {/* The rail the quote hangs off: what this band is,
                        and the way through the rest of it. */}
                    <div className="flex flex-col max-md:mb-6 max-md:gap-4 md:gap-[2.25rem] md:pt-[4.375rem]">
                        {/* The lead label as the Get Matched band sets it (DirectoryIntro's
                            eyebrow): 15px medium, tracked in rather than out, and
                            in sentence case — not the tracked-out caps this used. */}
                        <p
                            className={cn(
                                'm-0 font-sans text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.046875rem]',
                                dark ? 'text-[#a3a3a3]' : 'text-[#767676]',
                            )}
                        >
                            {label}
                        </p>

                        {quotes.length > 1 ? (
                            <div className="flex items-center gap-[0.75rem]">
                                <button
                                    type="button"
                                    aria-label="Previous review"
                                    onClick={() => step(-1)}
                                    className={arrowClass(dark)}
                                >
                                    <ChevronLeft className="size-[1.25rem]" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Next review"
                                    onClick={() => step(1)}
                                    className={arrowClass(dark)}
                                >
                                    <ChevronRight className="size-[1.25rem]" />
                                </button>
                                <span
                                    className={cn(
                                        'ml-[0.5rem] font-sans text-[0.875rem]',
                                        dark
                                            ? 'text-white/60'
                                            : 'text-[#6e6a62]',
                                    )}
                                >
                                    {index + 1} / {quotes.length}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <figure className="m-0 max-md:pt-8 md:pt-[4.375rem]">
                        <div ref={quoteRef}>
                            <blockquote className="m-0">
                                {/* Hidden until measured, so the first paint
                                    is never the oversized pass.
                                    `visibility`, not display: the box has to
                                    lay out for its height to be measurable at
                                    all. Desktop only — the mobile size is
                                    small enough that no quote overruns. */}
                                <p
                                    ref={bodyRef}
                                    style={
                                        expanded
                                            ? undefined
                                            : {
                                                  display: '-webkit-box',
                                                  WebkitBoxOrient: 'vertical',
                                                  WebkitLineClamp: QUOTE_LINES,
                                                  overflow: 'hidden',
                                              }
                                    }
                                    className={cn(
                                        'm-0 max-md:text-[1.5rem] max-md:leading-[1.25] md:text-[3.75rem]',
                                        // The project's tail speaks in the
                                        // editorial serif; the product keeps
                                        // the grotesk its band was drawn in.
                                        dark
                                            ? 'font-gestura font-normal text-[#e5e5e5] md:leading-[1.15] md:tracking-[-0.015em]'
                                            : 'font-sans font-medium text-[#323232] md:leading-[1.32] md:tracking-[-0.03em]',
                                    )}
                                >
                                    {`“${body}”`}
                                </p>
                            </blockquote>

                            {overflows && !expanded ? (
                                <button
                                    type="button"
                                    onClick={() => setExpanded(true)}
                                    className={cn(
                                        'mt-[1.25rem] cursor-pointer bg-transparent p-0 font-sans font-medium transition-colors max-md:text-base md:text-[1.125rem]',
                                        dark
                                            ? 'text-white hover:text-white/60'
                                            : 'text-[#323232] hover:text-[#767676]',
                                    )}
                                >
                                    Read more
                                </button>
                            ) : null}

                            {source || attribution ? (
                                <figcaption className="flex items-center font-sans max-md:mt-6 max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-1 max-md:text-sm md:mt-[4rem] md:gap-[1.125rem] md:text-[1.125rem]">
                                    {source ? (
                                        <span
                                            className={cn(
                                                'font-medium',
                                                dark
                                                    ? 'text-[#e5e5e5]'
                                                    : 'text-[#323232]',
                                            )}
                                        >
                                            {source}
                                        </span>
                                    ) : null}
                                    {source && attribution ? (
                                        <span
                                            aria-hidden="true"
                                            className={
                                                dark
                                                    ? 'text-white/30'
                                                    : 'text-[#ddd8d0]'
                                            }
                                        >
                                            —
                                        </span>
                                    ) : null}
                                    {attribution ? (
                                        <span
                                            className={
                                                dark
                                                    ? 'text-white/60'
                                                    : 'text-[#6e6a62]'
                                            }
                                        >
                                            {attribution}
                                        </span>
                                    ) : null}
                                </figcaption>
                            ) : null}
                        </div>
                    </figure>
                </div>
            </div>
        </section>
    );
};
